import { memo } from 'react';
import { useCopyToClipboard } from 'react-use';

import { type WorkspaceInfo } from '@air/api/types';
import { Tooltip } from '@air/primitive-tooltip';
import { cn } from '@air/utils-tailwind';

import DraggableWorkspaceItem from '~/components/MainNavigation/WorkspacesNav/DraggableWorkspaceItem';
import { WorkspaceNavButton } from '~/components/MainNavigation/WorkspacesNav/WorkspaceNavButton';
import { WorkspaceNavImage } from '~/components/MainNavigation/WorkspacesNav/WorkspacesNavImage';
import { WORKSPACE_NAV_WORKSPACE_BUTTON } from '~/constants/testIDs';

export type WorkspaceNavButtonProps = {
  index: number;
  workspace: WorkspaceInfo;
  onClick: () => void;
  isActive: boolean;
};

export const WorkspaceButton = memo(({ workspace, onClick, isActive, index }: WorkspaceNavButtonProps) => {
  const [_, copyToClipboard] = useCopyToClipboard();

  return (
    <DraggableWorkspaceItem index={index} workspace={workspace}>
      <Tooltip label={workspace.name} side="right">
        <WorkspaceNavButton
          className="relative mx-auto flex justify-center"
          onClick={onClick}
          // biome-ignore lint/performance/noJsxPropsBind: context menu copies workspace id inline for clipboard UX
          onContextMenu={(event) => {
            event.preventDefault();
            copyToClipboard(workspace.id);
          }}
          data-testid={WORKSPACE_NAV_WORKSPACE_BUTTON}
          data-workspace-id={workspace.id}
          data-workspace-name={workspace.name}
          state={isActive ? 'active' : 'default'}
        >
          <WorkspaceNavImage
            className={cn(
              isActive ? 'border ads:border-ads-gray-1 border-gray-1 opacity-100' : 'opacity-40',
              'transition-opacity hover:opacity-100',
            )}
            image={workspace.image}
            data-testid={workspace.name}
          />
        </WorkspaceNavButton>
      </Tooltip>
    </DraggableWorkspaceItem>
  );
});

WorkspaceButton.displayName = 'WorkspaceButton';
